import React, { useState, useEffect, useContext } from 'react';
import {
  DollarSign,
  CheckCircle,
  XCircle,
  Clock,
  Eye,
  TrendingUp,
  AlertCircle
} from 'lucide-react';
import { ThemeContext } from './theme';
import AlertModal from '../components/AlertModal';
import { LanguageContext } from "./LanguageContext";
import { apiFetch } from "../context/AuthContext";
import { useProfile } from "../hooks/useProfile";

const Mo3tiAdvancementManagement = () => {
  const { theme } = useContext(ThemeContext);
  const { language } = useContext(LanguageContext);
  const { profile } = useProfile();

  const [setLanguage] = useState(() => {
    return localStorage.getItem('language') || 'ar';
  });

  useEffect(() => {
    const handleLanguageChange = () => {
      setLanguage(localStorage.getItem('language') || 'ar');
    };
    window.addEventListener('languageChange', handleLanguageChange);
    return () => window.removeEventListener('languageChange', handleLanguageChange);
  }, []);

  const isRTL = language === 'ar';
  // Note: Authorization handled by apiFetch; include Accept-Language when needed


  // Translations
  const translations = {
    ar: {
      title: 'إدارة طلبات السلفة',
      pendingRequests: 'الطلبات المعلقة',
      approved: 'معتمد',
      disbursed: 'مصروف',
      totalAmount: 'المبلغ الإجمالي',
      pending: 'معلق',
      completed: 'مكتمل',
      rejected: 'مرفوض',
      all: 'الكل',
      employeeName: 'اسم الموظف',
      employeeCode: 'كود الموظف',
      amount: 'المبلغ',
      installments: 'الأقساط',
      reason: 'السبب',
      urgency: 'الأولوية',
      date: 'التاريخ',
      remaining: 'المتبقي',
      pleaseWait: 'الرجاء الانتظار أثناء جلب الطلبات',
      noRequests: 'لا توجد طلبات',
      tryAgain: 'حاول مرة أخرى',
      requestDetails: 'تفاصيل الطلب',
      requestedAmount: 'المبلغ المطلوب',
      numberOfInstallments: 'عدد الأقساط',
      installmentAmount: 'قيمة القسط',
      totalRepaid: 'المبلغ المسدد',
      remainingBalance: 'الرصيد المتبقي',
      installmentsPaid: 'الأقساط المسددة',
      status: 'الحالة',
      requestedDate: 'تاريخ الطلب',
      reviewedDate: 'تاريخ المراجعة',
      referenceNumber: 'رقم المرجع',
      repaymentStartDate: 'تاريخ بدء السداد',
      nextPaymentDate: 'تاريخ الدفع القادم',
      repaymentHistory: 'سجل السداد',
      installmentNum: 'القسط رقم',
      paymentDate: 'تاريخ الدفع',
      method: 'الطريقة',
      reference: 'المرجع',
      notes: 'ملاحظات',
      close: 'إغلاق',
      cancel: 'إلغاء',
      approve: 'موافقة',
      reject: 'رفض',
      processSolfa: 'معالجة طلب السلفة',
      disbursementMethodLabel: 'طريقة الصرف',
      referenceNumberRequired: 'رقم المرجع *',
      referenceNumberPlaceholder: 'أدخل رقم المعاملة',
      confirmDisbursement: 'تأكيد الصرف',
      bankTransfer: 'تحويل بنكي',
      cash: 'نقدي',
      check: 'شيك',
      normal: 'عادي',
      high: 'مرتفع',
      urgent: 'عاجل',
      unknownEmployee: 'موظف غير معروف',
      provideRejectionReason: 'يرجى تقديم سبب الرفض',
      provideReferenceNumber: 'يرجى تقديم رقم المرجع',
      requestApprovedSuccess: 'تمت الموافقة على طلب السلفة بنجاح!',
      requestRejected: 'تم رفض طلب السلفة',
      solfaDisbursedSuccess: 'تم صرف السلفة بنجاح!',
      approvalFailed: 'فشل الموافقة',
      rejectionFailed: 'فشل الرفض',
      disbursementFailed: 'فشل الصرف',
      refresh: 'تحديث',
      loading: 'جاري التحميل...',
      noRequestsFound: 'لا توجد طلبات',
      viewDetails: 'عرض التفاصيل',
      processRequest: 'معالجة الطلب',
      disburse: 'صرف',
      disburseSolfa: 'صرف السلفة',
      employee: 'الموظف',
      reviewedBy: 'تمت المراجعة بواسطة',
      approvalNotes: 'ملاحظات الموافقة',
      rejectionReason: 'سبب الرفض',
      disbursedDate: 'تاريخ الصرف',
      disbursementMethod: 'طريقة الصرف',
      approvalNotesOptional: 'ملاحظات الموافقة (اختياري)',
      approvalNotesPlaceholder: 'أضف أي ملاحظات حول هذه الموافقة...',
      rejectionReasonIfRejecting: 'سبب الرفض (في حالة الرفض)',
      rejectionReasonPlaceholder: 'اشرح سبب رفض هذا الطلب...',
    },
    en: {
      title: 'Solfa Requests Management',
      pendingRequests: 'Pending Requests',
      pending: 'Pending',
      loading: 'Loading...',
      pleaseWait: 'Please wait while requests are fetched',
      noRequests: 'No requests',
      noRequestsFound: 'No requests found',
      tryAgain: 'Try again',
      requestDetails: 'Request Details',
      requestedAmount: 'Requested Amount',
      numberOfInstallments: 'Number of Installments',
      installmentAmount: 'Installment Amount',
      totalRepaid: 'Total Repaid',
      remainingBalance: 'Remaining Balance',
      installmentsPaid: 'Installments Paid',
      viewDetails: 'View Details',
      processRequest: 'Process Request',
      disburse: 'Disburse',
      disburseSolfa: 'Disburse Solfa',
      approvalNotesOptional: 'Approval Notes (optional)',
      approved: 'Approved',
      disbursed: 'Disbursed',
      totalAmount: 'Total Amount',
      completed: 'Completed',
      rejected: 'Rejected',
      all: 'All',
      cancel: 'Cancel',
      close: 'Close',
      approve: 'Approve',
      reject: 'Reject',
      employeeName: 'Employee Name',
      employeeCode: 'Employee Code',
      amount: 'Amount',
      installments: 'Installments',
      reason: 'Reason',
      urgency: 'Urgency',
      date: 'Date',
      remaining: 'Remaining',
      employee: 'Employee',
      reviewedBy: 'Reviewed By',
      approvalNotes: 'Approval Notes',
      rejectionReason: 'Rejection Reason',
      disbursedDate: 'Disbursed Date',
      disbursementMethod: 'Disbursement Method',
      referenceNumber: 'Reference Number',
      repaymentStartDate: 'Repayment Start Date',
      nextPaymentDate: 'Next Payment Date',
      repaymentHistory: 'Repayment History',
      installmentNum: 'Installment #',
      paymentDate: 'Payment Date',
      method: 'Method',
      reference: 'Reference',
      notes: 'Notes',
      disbursementMethodLabel: 'Disbursement Method',
      referenceNumberRequired: 'Reference Number *',
      referenceNumberPlaceholder: 'Enter transaction reference',
      confirmDisbursement: 'Confirm Disbursement',
      bankTransfer: 'Bank Transfer',
      cash: 'Cash',
      check: 'Check',
      normal: 'Normal',
      high: 'High',
      urgent: 'Urgent',
      unknownEmployee: 'Unknown Employee',
      provideRejectionReason: 'Please provide a rejection reason',
      provideReferenceNumber: 'Please provide a reference number',
      requestApprovedSuccess: 'Solfa request approved successfully!',
      requestRejected: 'Solfa request rejected',
      solfaDisbursedSuccess: 'Solfa disbursed successfully!',
      approvalFailed: 'Approval failed',
      rejectionFailed: 'Rejection failed',
      disbursementFailed: 'Disbursement failed',
      refresh: 'Refresh',
    }
  };


  const t = translations[language];

  const [activeTab, setActiveTab] = useState('pending');
  const [requests, setRequests] = useState([]);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showProcessModal, setShowProcessModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [repaymentHistory, setRepaymentHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({
    totalPending: 0,
    totalApproved: 0,
    totalDisbursed: 0,
    totalAmount: 0
  });

  const [formData, setFormData] = useState({
    status: '',
    approvalNotes: '',
    rejectionReason: '',
    disbursementMethod: 'bank_transfer',
    referenceNumber: ''
  });

  // AlertModal State
  const [alertModal, setAlertModal] = useState({
    open: false,
    title: '',
    message: '',
    variant: 'info',
    showCancel: false,
    onConfirm: null,
  });

  const showAlert = (title, message, variant = 'info', onConfirm = null, showCancel = false) => {
    setAlertModal({
      open: true,
      title,
      message,
      variant,
      showCancel,
      onConfirm
    });
  };

  const closeAlert = () => {
    setAlertModal({ ...alertModal, open: false });
  };

  const BASE_URL = process.env.REACT_APP_BASE_URL;

  // Helper function to get employee name based on language
  const getEmployeeName = (request) => {
    if (language === 'ar') {
      return request.arabicName || request.employeeName || request.employee?.name_ar || request.employee?.sr || t.unknownEmployee;
    }
    return request.employeeName || request.employee?.name || request.employee?.sr || t.unknownEmployee;
  };

  // Configure axios with Accept-Language header
  // const getAxiosConfig = () => ({
  //   headers: {
  //     'Accept-Language': language === 'ar' ? 'ar' : 'en'
  //   }
  // });

  useEffect(() => {
    fetchRequests(activeTab);
    fetchStats();
  }, [activeTab, language]);

  useEffect(() => {
    const target = window.__notificationTarget;
    if (target?.advancementId && requests.length > 0) {
      const request = requests.find(r => r.id === Number(target.advancementId));
      if (request) {
        setSelectedRequest(request);
        setShowDetailsModal(true);
        window.__notificationTarget = null;
      }
    }
  }, [requests]);

  const buildAdvancementRequestsEndpoint = (status = 'all') => {
    const role = (profile?.role || localStorage.getItem('role') || '').toLowerCase();
    const isAdminRole = role === 'admin' || role === 'superadmin';
    const clientName = profile?.clientName || localStorage.getItem('clientName') || '';
    const managerCode = profile?.infinityCode || localStorage.getItem('infinityCode') || '';

    const params = new URLSearchParams();
    if (status !== 'all') {
      params.set('status', status);
    }
    params.set('clientName', clientName);

    if (!isAdminRole) {
      params.set('managerInfinityCode', managerCode);
    }

    return `${BASE_URL}/api/advancement-requests?${params.toString()}`;
  };

  const fetchRequests = async (status) => {
    try {
      setLoading(true);
      setError(null);
      const endpoint = buildAdvancementRequestsEndpoint(status);

      const res = await apiFetch(endpoint, { method: 'GET', headers: { 'Accept-Language': language === 'ar' ? 'ar' : 'en' } });
      if (!res.ok) throw new Error('Failed to fetch requests');
      const data = await res.json();
      const list = data?.data ?? data ?? [];
      setRequests(Array.isArray(list) ? list : []);
    } catch (error) {
      console.error('Failed to fetch requests:', error);
      setError(error?.message || 'Failed to load requests');
      setRequests([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const res = await apiFetch(
        buildAdvancementRequestsEndpoint('all'),
        { method: 'GET', headers: { 'Accept-Language': language === 'ar' ? 'ar' : 'en' } }
      );
      if (!res.ok) throw new Error('Failed to fetch stats');
      const payload = await res.json();
      const allRequests = payload?.data || payload || [];

      setStats({
        totalPending: allRequests.filter(r => r.status === 'pending').length,
        totalApproved: allRequests.filter(r => r.status === 'approved').length,
        totalDisbursed: allRequests.filter(r => r.status === 'disbursed').length,
        totalAmount: allRequests.reduce((sum, r) => sum + (r.requestedAmount || 0), 0)
      });
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const fetchRepaymentHistory = async (requestId) => {
    try {
      const res = await apiFetch(
        `${BASE_URL}/api/advancement-requests/${requestId}/repayments`,
        { method: 'GET', headers: { 'Accept-Language': language === 'ar' ? 'ar' : 'en' } }
      );
      if (!res.ok) throw new Error('Failed to fetch repayments');
      const data = await res.json();
      setRepaymentHistory(data?.data || data || []);
    } catch (error) {
      console.error('Failed to fetch repayment history:', error);
      setRepaymentHistory([]);
    }
  };

  const handleApprove = async () => {
    try {
      const adminCode = profile?.infinityCode || localStorage.getItem('infinityCode');
      const res = await apiFetch(
        `${BASE_URL}/api/advancement-requests/${selectedRequest.id}/status`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'Accept-Language': language === 'ar' ? 'ar' : 'en',
          },
          body: JSON.stringify({
            status: 'approved',
            reviewedBy: adminCode,
            approvalNotes: formData.approvalNotes,
          }),
        }
      );
      if (!res.ok) throw new Error('Approval failed');

      showAlert(t.approve, t.requestApprovedSuccess, 'success', () => {
        fetchRequests(activeTab);
        fetchStats();
        setShowProcessModal(false);
        resetForm();
      });
    } catch (error) {
      console.error('Approval failed:', error);
      showAlert(t.approvalFailed, error?.message || t.approvalFailed, 'error');
    }
  };

  const handleReject = async () => {
    if (!formData.rejectionReason.trim()) {
      showAlert(t.reject, t.provideRejectionReason, 'error');
      return;
    }

    try {
      const adminCode = profile?.infinityCode || localStorage.getItem('infinityCode');
      const res = await apiFetch(
        `${BASE_URL}/api/advancement-requests/${selectedRequest.id}/status`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'Accept-Language': language === 'ar' ? 'ar' : 'en',
          },
          body: JSON.stringify({
            status: 'rejected',
            reviewedBy: adminCode,
            rejectionReason: formData.rejectionReason,
          }),
        }
      );
      if (!res.ok) throw new Error('Rejection failed');

      showAlert(t.reject, t.requestRejected, 'success', () => {
        fetchRequests(activeTab);
        fetchStats();
        setShowProcessModal(false);
        resetForm();
      });
    } catch (error) {
      console.error('Rejection failed:', error);
      showAlert(t.rejectionFailed, error?.message || t.rejectionFailed, 'error');
    }
  };

  const handleDisburse = async () => {
    if (!formData.referenceNumber.trim()) {
      showAlert(t.disburse, t.provideReferenceNumber, 'error');
      return;
    }

    try {
      const adminCode = profile?.infinityCode || localStorage.getItem('infinityCode');
      const res = await apiFetch(
        `${BASE_URL}/api/advancement-requests/${selectedRequest.id}/status`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'Accept-Language': language === 'ar' ? 'ar' : 'en',
          },
          body: JSON.stringify({
            status: 'disbursed',
            reviewedBy: adminCode,
            disbursementMethod: formData.disbursementMethod,
            referenceNumber: formData.referenceNumber,
          }),
        }
      );
      if (!res.ok) throw new Error('Disbursement failed');

      showAlert(t.disburse, t.solfaDisbursedSuccess, 'success', () => {
        fetchRequests(activeTab);
        fetchStats();
        setShowProcessModal(false);
        resetForm();
      });
    } catch (error) {
      console.error('Disbursement failed:', error);
      showAlert(t.disbursementFailed, error?.message || t.disbursementFailed, 'error');
    }
  };

  const resetForm = () => {
    setFormData({
      status: '',
      approvalNotes: '',
      rejectionReason: '',
      disbursementMethod: 'bank_transfer',
      referenceNumber: ''
    });
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'approved': return <CheckCircle size={18} color="#10b981" />;
      case 'rejected': return <XCircle size={18} color="#ef4444" />;
      case 'disbursed': return <DollarSign size={18} color="#2e7cba" />;
      case 'completed': return <CheckCircle size={18} color="#8b5cf6" />;
      case 'pending': return <Clock size={18} color="#f59e0b" />;
      default: return <AlertCircle size={18} />;
    }
  };

  const getStatusText = (status) => {
    const statusMap = {
      pending: t.pending,
      approved: t.approved,
      rejected: t.rejected,
      disbursed: t.disbursed,
      completed: t.completed
    };
    return statusMap[status] || status;
  };

  const getUrgencyText = (urgency) => {
    const urgencyMap = {
      normal: t.normal,
      high: t.high,
      urgent: t.urgent
    };
    return urgencyMap[urgency] || urgency || t.normal;
  };

  const styles = {
    container: {
      padding: "clamp(16px, 3vw, 24px)",
      direction: isRTL ? 'ltr' : 'ltr',
      marginTop: 0,
    },

    statsGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
      gap: "16px",
      marginBottom: "24px",
      marginTop: -60,
    },

    statCard: {
      background: theme === "light"
        ? "rgba(255, 255, 255, 1)"
        : "rgba(255, 255, 255, 0.01)",
      backdropFilter: "blur(20px)",
      border: theme === "light"
        ? "1px solid rgba(0, 0, 0, 0.1)"
        : "1px solid rgba(255, 255, 255, 0.1)",
      borderRadius: "16px",
      padding: "20px",
      display: "flex",
      alignItems: "center",
      gap: "16px",
      direction: isRTL ? 'ltr' : 'ltr',
    },

    statIcon: {
      width: "48px",
      height: "48px",
      borderRadius: "12px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      background: "linear-gradient(135deg, #2e7cba, #2e7cba)",
      flexShrink: 0,
    },

    statInfo: {
      flex: 1,
      textAlign: isRTL ? 'right' : 'left',
    },

    statValue: {
      fontSize: "24px",
      fontWeight: "700",
      color: theme === "light" ? "#0f172a" : "#ffffff",
      margin: "0",
    },

    statLabel: {
      fontSize: "14px",
      color: theme === "light" ? "#64748b" : "rgba(255, 255, 255, 0.7)",
      margin: "4px 0 0 0",
    },

    card: {
      background: theme === "light"
        ? "rgba(255, 255, 255, 1)"
        : "rgba(255, 255, 255, 0.01)",
      backdropFilter: "blur(20px)",
      border: theme === "light"
        ? "1px solid rgba(0, 0, 0, 0.1)"
        : "1px solid rgba(255, 255, 255, 0.1)",
      borderRadius: "16px",
      padding: "24px",
      direction: isRTL ? 'ltr' : 'ltr',
    },

    title: {
      fontSize: "24px",
      fontWeight: "700",
      color: theme === "light" ? "#0f172a" : "#ffffff",
      margin: "0 0 20px 0",
      display: "flex",
      alignItems: "center",
      gap: "12px",
      flexDirection: isRTL ? 'row-reverse' : 'row',
      marginBottom: "24px",

    },

    tabContainer: {
      display: "flex",
      gap: "8px",
      marginBottom: "24px",
      borderBottom: theme === "light"
        ? "2px solid #e2e8f0"
        : "2px solid rgba(255, 255, 255, 0.1)",
      paddingBottom: "0",
      overflowX: "auto",
      flexDirection: isRTL ? 'row-reverse' : 'row',

    },

    tab: (isActive) => ({
      padding: "12px 24px",
      border: "none",


      background: "transparent",
      color: isActive
        ? (theme === "light" ? "#2e7cba" : "#60a5fa")
        : (theme === "light" ? "#64748b" : "rgba(255, 255, 255, 0.6)"),
      fontSize: "14px",
      fontWeight: "600",
      cursor: "pointer",
      borderBottom: isActive ? "3px solid #2e7cba" : "none",
      marginBottom: "2px",
      transition: "all 0.3s ease",
      whiteSpace: "nowrap",
    }),

    requestCard: {
      padding: "20px",
      border: theme === "light"
        ? "2px solid #e2e8f0"
        : "2px solid rgba(255, 255, 255, 0.15)",
      borderRadius: "12px",
      marginBottom: "16px",
      background: theme === "light"
        ? "#ffffff"
        : "rgba(255, 255, 255, 0.02)",
      transition: "all 0.3s ease",
      direction: isRTL ? 'ltr' : 'ltr',
    },

    requestHeader: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "flex-start",
      flexWrap: "wrap",
      gap: "16px",
      marginBottom: "16px",
      flexDirection: isRTL ? 'row' : 'row',
    },

    requestInfo: {
      flex: 1,
      minWidth: "250px",
      textAlign: isRTL ? 'right' : 'left',
    },

    employeeName: {
      fontSize: "18px",
      fontWeight: "600",
      color: theme === "light" ? "#0f172a" : "#ffffff",
      margin: "0 0 8px 0",
    },

    requestDetail: {
      fontSize: "14px",
      color: theme === "light" ? "#64748b" : "rgba(255, 255, 255, 0.7)",
      margin: "6px 0",
      display: "flex",
      alignItems: "center",
      gap: "8px",
      flexDirection: isRTL ? 'row' : 'row',
    },

    statusBadge: (status) => ({
      display: "inline-flex",
      alignItems: "center",
      gap: "6px",
      padding: "6px 12px",
      borderRadius: "20px",
      fontSize: "12px",
      fontWeight: "600",
      background: status === 'approved' ? 'rgba(16, 185, 129, 0.1)' :
        status === 'rejected' ? 'rgba(239, 68, 68, 0.1)' :
          status === 'disbursed' ? 'rgba(59, 130, 246, 0.1)' :
            status === 'completed' ? 'rgba(139, 92, 246, 0.1)' :
              'rgba(245, 158, 11, 0.1)',
      color: status === 'approved' ? '#10b981' :
        status === 'rejected' ? '#ef4444' :
          status === 'disbursed' ? '#2e7cba' :
            status === 'completed' ? '#8b5cf6' :
              '#f59e0b',
      flexDirection: isRTL ? 'row' : 'row',
    }),

    buttonGroup: {
      display: "flex",
      gap: "8px",
      flexWrap: "wrap",
      flexDirection: isRTL ? 'row' : 'row',
    },

    button: {
      background: "linear-gradient(135deg, #2e7cba, #2e7cba)",
      color: "white",
      border: "none",
      borderRadius: "10px",
      padding: "10px 20px",
      fontSize: "14px",
      fontWeight: "600",
      cursor: "pointer",
      transition: "all 0.3s ease",
      display: "flex",
      alignItems: "center",
      gap: "8px",
      flexDirection: isRTL ? 'row' : 'row',
    },

    secondaryButton: {
      background: theme === "light"
        ? "rgba(0, 0, 0, 0.05)"
        : "rgba(255, 255, 255, 0.1)",
      color: theme === "light" ? "#374151" : "#ffffff",
      border: "none",
      borderRadius: "10px",
      padding: "10px 20px",
      fontSize: "14px",
      fontWeight: "600",
      cursor: "pointer",
      transition: "all 0.3s ease",
      display: "flex",
      alignItems: "center",
      gap: "8px",
      flexDirection: isRTL ? 'row' : 'row',
    },

    rejectButton: {
      background: "linear-gradient(135deg, #ef4444, #dc2626)",
      color: "white",
      border: "none",
      borderRadius: "10px",
      padding: "10px 20px",
      fontSize: "14px",
      fontWeight: "600",
      cursor: "pointer",
      transition: "all 0.3s ease",
      display: "flex",
      alignItems: "center",
      gap: "8px",
      flexDirection: isRTL ? 'row' : 'row',
    },

    emptyState: {
      textAlign: "center",
      padding: "60px 20px",
      color: theme === "light" ? "#64748b" : "rgba(255, 255, 255, 0.6)",
    },

    errorState: {
      textAlign: "center",
      padding: "60px 20px",
      color: "#ef4444",
    },

    loadingState: {
      textAlign: "center",
      padding: "60px 20px",
      color: theme === "light" ? "#64748b" : "rgba(255, 255, 255, 0.6)",
    },

    modal: {
      position: "fixed",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
      backgroundColor: "rgba(0, 0, 0, 0.5)",
      backdropFilter: "blur(6px)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 1000,
      padding: "20px",
    },

    modalContent: {
      background: theme === "light"
        ? "rgba(255, 255, 255, 0.95)"
        : "rgba(30, 41, 59, 0.95)",
      padding: "32px",
      borderRadius: "20px",
      width: "100%",
      maxWidth: "600px",
      maxHeight: "85vh",
      overflowY: "auto",
      boxShadow: theme === "light"
        ? "0 10px 40px rgba(0, 0, 0, 0.15)"
        : "0 10px 40px rgba(0, 0, 0, 0.5)",
      color: theme === "light" ? "#0f172a" : "#ffffff",
      direction: isRTL ? 'ltr' : 'ltr',
    },

    modalTitle: {
      fontSize: "20px",
      fontWeight: "700",
      marginBottom: "24px",
      color: theme === "light" ? "#0f172a" : "#ffffff",
      textAlign: isRTL ? 'right' : 'left',
    },

    formGroup: {
      marginBottom: "20px",
    },

    label: {
      display: "block",
      fontSize: "14px",
      fontWeight: "600",
      marginBottom: "8px",
      color: theme === "light" ? "#374151" : "rgba(255, 255, 255, 0.9)",
      textAlign: isRTL ? 'right' : 'left',
    },

    textarea: {
      width: "100%",
      minHeight: "100px",
      padding: "12px",
      border: theme === "light"
        ? "2px solid #e2e8f0"
        : "2px solid rgba(255, 255, 255, 0.2)",
      borderRadius: "12px",
      background: theme === "light" ? "#f8fafc" : "rgba(255, 255, 255, 0.05)",
      color: theme === "light" ? "#0f172a" : "#ffffff",
      fontSize: "14px",
      fontFamily: "inherit",
      resize: "vertical",
      outline: "none",
      transition: "border-color 0.3s ease",
      boxSizing: "border-box",
      direction: isRTL ? 'ltr' : 'ltr',
      textAlign: isRTL ? 'right' : 'left',
    },

    input: {
      width: "100%",
      padding: "12px",
      border: theme === "light"
        ? "2px solid #e2e8f0"
        : "2px solid rgba(255, 255, 255, 0.2)",
      borderRadius: "12px",
      background: theme === "light" ? "#f8fafc" : "rgba(255, 255, 255, 0.05)",
      color: theme === "light" ? "#0f172a" : "#ffffff",
      fontSize: "14px",
      fontFamily: "inherit",
      outline: "none",
      transition: "border-color 0.3s ease",
      boxSizing: "border-box",
      direction: isRTL ? 'ltr' : 'ltr',
      textAlign: isRTL ? 'right' : 'left',
    },

    select: {
      width: "100%",
      padding: "12px",
      border: theme === "light"
        ? "2px solid #e2e8f0"
        : "2px solid rgba(255, 255, 255, 0.2)",
      borderRadius: "12px",
      background: theme === "light" ? "#f8fafc" : "rgba(255, 255, 255, 0.05)",
      color: theme === "light" ? "#0f172a" : "#ffffff",
      fontSize: "14px",
      fontFamily: "inherit",
      appearance: "none",  // ضيف السطر ده
      WebkitAppearance: "none",  // وده
      MozAppearance: "none",  // وده
      outline: "none",
      cursor: "pointer",
      boxSizing: "border-box",
      direction: isRTL ? 'ltr' : 'ltr',
    },

    detailRow: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      padding: "12px 0",
      borderBottom: theme === "light"
        ? "1px solid #e2e8f0"
        : "1px solid rgba(255, 255, 255, 0.1)",
      flexDirection: isRTL ? 'row' : 'row',
    },

    detailLabel: {
      fontSize: "14px",
      fontWeight: "600",
      color: theme === "light" ? "#64748b" : "rgba(255, 255, 255, 0.7)",
      textAlign: isRTL ? 'right' : 'left',
    },

    detailValue: {
      fontSize: "14px",
      fontWeight: "500",
      color: theme === "light" ? "#0f172a" : "#ffffff",
      textAlign: isRTL ? 'left' : 'right',
    },
  };

  const tabs = [
    { id: 'pending', label: t.pending, count: stats.totalPending },
    { id: 'approved', label: t.approved, count: stats.totalApproved },
    { id: 'disbursed', label: t.disbursed, count: stats.totalDisbursed },
    { id: 'completed', label: t.completed, count: 0 },
    { id: 'rejected', label: t.rejected, count: 0 },
    { id: 'all', label: t.all, count: requests.length },
  ];

  return (
    <div style={styles.container}>
      {/* Statistics Cards */}
      <div style={styles.statsGrid}>
        <div style={styles.statCard}>
          <div style={styles.statIcon}>
            <Clock size={24} color="white" />
          </div>
          <div style={styles.statInfo}>
            <p style={styles.statValue}>{stats.totalPending}</p>
            <p style={styles.statLabel}>{t.pendingRequests}</p>
          </div>
        </div>

        <div style={styles.statCard}>
          <div style={{ ...styles.statIcon, background: "linear-gradient(135deg, #10b981, #059669)" }}>
            <CheckCircle size={24} color="white" />
          </div>
          <div style={styles.statInfo}>
            <p style={styles.statValue}>{stats.totalApproved}</p>
            <p style={styles.statLabel}>{t.approved}</p>
          </div>
        </div>

        <div style={styles.statCard}>
          <div style={{ ...styles.statIcon, background: "linear-gradient(135deg, #8b5cf6, #7c3aed)" }}>
            <DollarSign size={24} color="white" />
          </div>
          <div style={styles.statInfo}>
            <p style={styles.statValue}>{stats.totalDisbursed}</p>
            <p style={styles.statLabel}>{t.disbursed}</p>
          </div>
        </div>

        <div style={styles.statCard}>
          <div style={{ ...styles.statIcon, background: "linear-gradient(135deg, #f59e0b, #d97706)" }}>
            <TrendingUp size={24} color="white" />
          </div>
          <div style={styles.statInfo}>
            <p style={styles.statValue}>
              {language === 'ar' ? `${stats.totalAmount.toLocaleString('ar-EG')} ج.م` : `EGP ${stats.totalAmount.toLocaleString()}`}
            </p>
            <p style={styles.statLabel}>{t.totalAmount}</p>
          </div>
        </div>
      </div>

      {/* Main Card */}
      <div style={styles.card}>
        <h2 style={styles.title}>
          <DollarSign size={24} />
          {t.title}
        </h2>

        {/* Tabs */}
        <div style={styles.tabContainer}>
          {tabs.map(tab => (
            <button
              key={tab.id}
              style={styles.tab(activeTab === tab.id)}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label} {tab.count > 0 && `(${tab.count})`}
            </button>
          ))}
          {/* <button
            style={styles.secondaryButton}
            onClick={() => fetchRequests(activeTab)}
            title={t.refresh}
          >
            <RefreshCw size={16} />
          </button> */}
        </div>

        {/* Loading State */}
        {loading && (
          <div style={styles.loadingState}>
            <Clock size={64} style={{ marginBottom: '20px', opacity: 0.3 }} />
            <h3 style={{ fontSize: "20px", fontWeight: "600", marginBottom: "8px" }}>
              {t.loading}
            </h3>
            <p>{t.pleaseWait}</p>
          </div>
        )}

        {/* Error State */}
        {error && !loading && (
          <div style={styles.errorState}>
            <AlertCircle size={64} style={{ marginBottom: '20px' }} />
            <h3 style={{ fontSize: "20px", fontWeight: "600", marginBottom: "8px" }}>
              {t.noRequests}
            </h3>
            <p>{error}</p>
            <button
              style={{ ...styles.button, marginTop: "16px" }}
              onClick={() => fetchRequests(activeTab)}
            >
              {t.tryAgain}
            </button>
          </div>
        )}

        {/* Requests List */}
        {!loading && !error && requests.length === 0 && (
          <div style={styles.emptyState}>
            <DollarSign size={64} style={{ marginBottom: '20px', opacity: 0.3 }} />
            <h3 style={{
              fontSize: "20px",
              fontWeight: "600",
              marginBottom: "8px",
              color: theme === "light" ? "#374151" : "rgba(255, 255, 255, 0.8)"
            }}>
              {t.noRequests}
            </h3>
            <p>{t.noRequestsFound}</p>
          </div>
        )}

        {!loading && !error && requests.length > 0 && requests.map(request => (
          <div key={request.id} style={styles.requestCard}>
            <div style={styles.requestHeader}>
              <div style={styles.requestInfo}>
                <h4 style={styles.employeeName}>
                  {getEmployeeName(request)}
                </h4>
                <p style={styles.requestDetail}>
                  <strong>{t.employeeCode}:</strong> {request.infinityCode}
                </p>
                <p style={styles.requestDetail}>
                  <strong>{t.amount}:</strong> {language === 'ar'
                    ? `${request.requestedAmount?.toLocaleString('ar-EG')} ج.م`
                    : `EGP ${request.requestedAmount?.toLocaleString()}`}
                </p>
                <p style={styles.requestDetail}>
                  <strong>{t.installments}:</strong> {request.numberOfInstallments || 'N/A'}
                  {request.installmentAmount && (language === 'ar'
                    ? ` (${request.installmentAmount.toLocaleString('ar-EG')} ج.م لكل قسط)`
                    : ` (EGP ${request.installmentAmount.toLocaleString()} each)`)}
                </p>
                <p style={styles.requestDetail}>
                  <strong>{t.reason}:</strong> {request.reason || 'N/A'}
                </p>
                <p style={styles.requestDetail}>
                  <strong>{t.urgency}:</strong> {getUrgencyText(request.urgency)}
                </p>
                <p style={styles.requestDetail}>
                  <strong>{t.date}:</strong> {new Date(request.requestedDate || request.created_at).toLocaleDateString(language === 'ar' ? 'ar-EG' : 'en-US')}
                </p>
                {request.remainingBalance !== undefined && (
                  <p style={styles.requestDetail}>
                    <strong>{t.remaining}:</strong> {language === 'ar'
                      ? `${request.remainingBalance?.toLocaleString('ar-EG')} ج.م`
                      : `EGP ${request.remainingBalance?.toLocaleString()}`}
                  </p>
                )}
              </div>
              <div>
                <div style={styles.statusBadge(request.status)}>
                  {getStatusIcon(request.status)}
                  {getStatusText(request.status)}
                </div>
              </div>
            </div>

            <div style={styles.buttonGroup}>
              <button
                style={styles.secondaryButton}
                onClick={() => {
                  setSelectedRequest(request);
                  fetchRepaymentHistory(request.id);
                  setShowDetailsModal(true);
                }}
              >
                <Eye size={16} /> {t.viewDetails}
              </button>

              {request.status === 'pending' && (
                <button
                  style={styles.button}
                  onClick={() => {
                    setSelectedRequest(request);
                    setFormData({ ...formData, status: '' });
                    setShowProcessModal(true);
                  }}
                >
                  {t.processRequest}
                </button>
              )}

              {request.status === 'approved' && (
                <button
                  style={styles.button}
                  onClick={() => {
                    setSelectedRequest(request);
                    setFormData({ ...formData, status: 'disburse' });
                    setShowProcessModal(true);
                  }}
                >
                  <DollarSign size={16} /> {t.disburse}
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Process Modal */}
      {showProcessModal && selectedRequest && (
        <div style={styles.modal} onClick={() => setShowProcessModal(false)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <h3 style={styles.modalTitle}>
              {formData.status === 'disburse' ? t.disburseSolfa : t.processSolfa}
            </h3>

            {/* Request Summary */}
            <div style={{ marginBottom: "24px" }}>
              <div style={styles.detailRow}>
                <span style={styles.detailLabel}>{t.employee}:</span>
                <span style={styles.detailValue}>{getEmployeeName(selectedRequest)}</span>
              </div>
              <div style={styles.detailRow}>
                <span style={styles.detailLabel}>{t.amount}:</span>
                <span style={styles.detailValue}>
                  {language === 'ar'
                    ? `${selectedRequest.requestedAmount?.toLocaleString('ar-EG')} ج.م`
                    : `EGP ${selectedRequest.requestedAmount?.toLocaleString()}`}
                </span>
              </div>
              <div style={styles.detailRow}>
                <span style={styles.detailLabel}>{t.installments}:</span>
                <span style={styles.detailValue}>{selectedRequest.numberOfInstallments || 'N/A'}</span>
              </div>
            </div>

            {formData.status === 'disburse' ? (
              <>
                <div style={styles.formGroup}>
                  <label style={styles.label}>{t.disbursementMethodLabel}</label>
                  <select
                    style={styles.select}
                    value={formData.disbursementMethod}
                    onChange={(e) => setFormData({ ...formData, disbursementMethod: e.target.value })}
                  >
                    <option value="bank_transfer">{t.bankTransfer}</option>
                    <option value="cash">{t.cash}</option>
                    <option value="check">{t.check}</option>
                  </select>
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>{t.referenceNumberRequired}</label>
                  <input
                    type="text"
                    style={styles.input}
                    value={formData.referenceNumber}
                    onChange={(e) => setFormData({ ...formData, referenceNumber: e.target.value })}
                    placeholder={t.referenceNumberPlaceholder}
                  />
                </div>

                <div style={{ ...styles.buttonGroup, justifyContent: isRTL ? "flex-start" : "flex-end", marginTop: "24px" }}>
                  <button
                    style={styles.secondaryButton}
                    onClick={() => {
                      setShowProcessModal(false);
                      resetForm();
                    }}
                  >
                    {t.cancel}
                  </button>
                  <button
                    style={styles.button}
                    onClick={handleDisburse}
                  >
                    {t.confirmDisbursement}
                  </button>
                </div>
              </>
            ) : (
              <>
                <div style={styles.formGroup}>
                  <label style={styles.label}>{t.approvalNotesOptional}</label>
                  <textarea
                    style={styles.textarea}
                    value={formData.approvalNotes}
                    onChange={(e) => setFormData({ ...formData, approvalNotes: e.target.value })}
                    placeholder={t.approvalNotesPlaceholder}
                  />
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>{t.rejectionReasonIfRejecting}</label>
                  <textarea
                    style={styles.textarea}
                    value={formData.rejectionReason}
                    onChange={(e) => setFormData({ ...formData, rejectionReason: e.target.value })}
                    placeholder={t.rejectionReasonPlaceholder}
                  />
                </div>

                <div style={{ ...styles.buttonGroup, justifyContent: isRTL ? "flex-start" : "flex-end", marginTop: "24px" }}>
                  <button
                    style={styles.secondaryButton}
                    onClick={() => {
                      setShowProcessModal(false);
                      resetForm();
                    }}
                  >
                    {t.cancel}
                  </button>
                  <button
                    style={styles.rejectButton}
                    onClick={handleReject}
                  >
                    <XCircle size={16} /> {t.reject}
                  </button>
                  <button
                    style={styles.button}
                    onClick={handleApprove}
                  >
                    <CheckCircle size={16} /> {t.approve}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Details Modal */}
      {showDetailsModal && selectedRequest && (
        <div style={styles.modal} onClick={() => setShowDetailsModal(false)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <h3 style={styles.modalTitle}>{t.requestDetails}</h3>

            <div style={{ marginBottom: "24px" }}>
              <div style={styles.detailRow}>
                <span style={styles.detailLabel}>{t.employeeName}:</span>
                <span style={styles.detailValue}>{getEmployeeName(selectedRequest)}</span>
              </div>
              <div style={styles.detailRow}>
                <span style={styles.detailLabel}>{t.employeeCode}:</span>
                <span style={styles.detailValue}>{selectedRequest.infinityCode}</span>
              </div>
              <div style={styles.detailRow}>
                <span style={styles.detailLabel}>{t.requestedAmount}:</span>
                <span style={styles.detailValue}>
                  {language === 'ar'
                    ? `${selectedRequest.requestedAmount?.toLocaleString('ar-EG')} ج.م`
                    : `EGP ${selectedRequest.requestedAmount?.toLocaleString()}`}
                </span>
              </div>
              <div style={styles.detailRow}>
                <span style={styles.detailLabel}>{t.numberOfInstallments}:</span>
                <span style={styles.detailValue}>{selectedRequest.numberOfInstallments || 'N/A'}</span>
              </div>
              <div style={styles.detailRow}>
                <span style={styles.detailLabel}>{t.installmentAmount}:</span>
                <span style={styles.detailValue}>
                  {selectedRequest.installmentAmount
                    ? (language === 'ar'
                      ? `${selectedRequest.installmentAmount.toLocaleString('ar-EG')} ج.م`
                      : `EGP ${selectedRequest.installmentAmount.toLocaleString()}`)
                    : 'N/A'}
                </span>
              </div>
              <div style={styles.detailRow}>
                <span style={styles.detailLabel}>{t.totalRepaid}:</span>
                <span style={styles.detailValue}>
                  {language === 'ar'
                    ? `${(selectedRequest.totalRepaid || 0).toLocaleString('ar-EG')} ج.م`
                    : `EGP ${(selectedRequest.totalRepaid || 0).toLocaleString()}`}
                </span>
              </div>
              <div style={styles.detailRow}>
                <span style={styles.detailLabel}>{t.remainingBalance}:</span>
                <span style={styles.detailValue}>
                  {language === 'ar'
                    ? `${(selectedRequest.remainingBalance || 0).toLocaleString('ar-EG')} ج.م`
                    : `EGP ${(selectedRequest.remainingBalance || 0).toLocaleString()}`}
                </span>
              </div>
              <div style={styles.detailRow}>
                <span style={styles.detailLabel}>{t.installmentsPaid}:</span>
                <span style={styles.detailValue}>
                  {selectedRequest.installmentsPaid || 0} / {selectedRequest.numberOfInstallments || 0}
                </span>
              </div>
              <div style={styles.detailRow}>
                <span style={styles.detailLabel}>{t.reason}:</span>
                <span style={styles.detailValue}>{selectedRequest.reason || 'N/A'}</span>
              </div>
              <div style={styles.detailRow}>
                <span style={styles.detailLabel}>{t.urgency}:</span>
                <span style={styles.detailValue}>{getUrgencyText(selectedRequest.urgency)}</span>
              </div>
              <div style={styles.detailRow}>
                <span style={styles.detailLabel}>{t.status}:</span>
                <span style={styles.statusBadge(selectedRequest.status)}>
                  {getStatusIcon(selectedRequest.status)}
                  {getStatusText(selectedRequest.status)}
                </span>
              </div>
              <div style={styles.detailRow}>
                <span style={styles.detailLabel}>{t.requestedDate}:</span>
                <span style={styles.detailValue}>
                  {new Date(selectedRequest.requestedDate || selectedRequest.created_at).toLocaleDateString(language === 'ar' ? 'ar-EG' : 'en-US')}
                </span>
              </div>
              {selectedRequest.reviewedDate && (
                <div style={styles.detailRow}>
                  <span style={styles.detailLabel}>{t.reviewedDate}:</span>
                  <span style={styles.detailValue}>
                    {new Date(selectedRequest.reviewedDate).toLocaleDateString(language === 'ar' ? 'ar-EG' : 'en-US')}
                  </span>
                </div>
              )}
              {selectedRequest.reviewerName && (
                <div style={styles.detailRow}>
                  <span style={styles.detailLabel}>{t.reviewedBy}:</span>
                  <span style={styles.detailValue}>{selectedRequest.reviewerName}</span>
                </div>
              )}
              {selectedRequest.approvalNotes && (
                <div style={styles.detailRow}>
                  <span style={styles.detailLabel}>{t.approvalNotes}:</span>
                  <span style={styles.detailValue}>{selectedRequest.approvalNotes}</span>
                </div>
              )}
              {selectedRequest.rejectionReason && (
                <div style={styles.detailRow}>
                  <span style={styles.detailLabel}>{t.rejectionReason}:</span>
                  <span style={styles.detailValue}>{selectedRequest.rejectionReason}</span>
                </div>
              )}
              {selectedRequest.disbursedDate && (
                <div style={styles.detailRow}>
                  <span style={styles.detailLabel}>{t.disbursedDate}:</span>
                  <span style={styles.detailValue}>
                    {new Date(selectedRequest.disbursedDate).toLocaleDateString(language === 'ar' ? 'ar-EG' : 'en-US')}
                  </span>
                </div>
              )}
              {selectedRequest.disbursementMethod && (
                <div style={styles.detailRow}>
                  <span style={styles.detailLabel}>{t.disbursementMethod}:</span>
                  <span style={styles.detailValue}>{selectedRequest.disbursementMethod}</span>
                </div>
              )}
              {selectedRequest.referenceNumber && (
                <div style={styles.detailRow}>
                  <span style={styles.detailLabel}>{t.referenceNumber}:</span>
                  <span style={styles.detailValue}>{selectedRequest.referenceNumber}</span>
                </div>
              )}
              {selectedRequest.repaymentStartDate && (
                <div style={styles.detailRow}>
                  <span style={styles.detailLabel}>{t.repaymentStartDate}:</span>
                  <span style={styles.detailValue}>
                    {new Date(selectedRequest.repaymentStartDate).toLocaleDateString(language === 'ar' ? 'ar-EG' : 'en-US')}
                  </span>
                </div>
              )}
              {selectedRequest.nextPaymentDate && (
                <div style={styles.detailRow}>
                  <span style={styles.detailLabel}>{t.nextPaymentDate}:</span>
                  <span style={styles.detailValue}>
                    {new Date(selectedRequest.nextPaymentDate).toLocaleDateString(language === 'ar' ? 'ar-EG' : 'en-US')}
                  </span>
                </div>
              )}
            </div>

            {/* Repayment History */}
            {repaymentHistory.length > 0 && (
              <>
                <h4 style={{
                  fontSize: "16px",
                  fontWeight: "600",
                  marginBottom: "16px",
                  color: theme === "light" ? "#0f172a" : "#ffffff",
                  textAlign: isRTL ? 'right' : 'left',
                }}>
                  {t.repaymentHistory}
                </h4>

                {repaymentHistory.map((payment) => (
                  <div
                    key={payment.id}
                    style={{
                      ...styles.requestCard,
                      marginBottom: "12px",
                      padding: "16px"
                    }}
                  >
                    <div style={styles.detailRow}>
                      <span style={styles.detailLabel}>{t.installmentNum} {payment.installmentNumber}</span>
                      <span style={styles.detailValue}>
                        {language === 'ar'
                          ? `${payment.amount?.toLocaleString('ar-EG')} ج.م`
                          : `EGP ${payment.amount?.toLocaleString()}`}
                      </span>
                    </div>
                    <div style={styles.detailRow}>
                      <span style={styles.detailLabel}>{t.paymentDate}:</span>
                      <span style={styles.detailValue}>
                        {new Date(payment.paymentDate).toLocaleDateString(language === 'ar' ? 'ar-EG' : 'en-US')}
                      </span>
                    </div>
                    <div style={styles.detailRow}>
                      <span style={styles.detailLabel}>{t.method}:</span>
                      <span style={styles.detailValue}>{payment.paymentMethod || 'N/A'}</span>
                    </div>
                    {payment.referenceNumber && (
                      <div style={styles.detailRow}>
                        <span style={styles.detailLabel}>{t.reference}:</span>
                        <span style={styles.detailValue}>{payment.referenceNumber}</span>
                      </div>
                    )}
                    {payment.notes && (
                      <div style={styles.detailRow}>
                        <span style={styles.detailLabel}>{t.notes}:</span>
                        <span style={styles.detailValue}>{payment.notes}</span>
                      </div>
                    )}
                  </div>
                ))}
              </>
            )}

            <div style={{ ...styles.buttonGroup, justifyContent: isRTL ? "flex-start" : "flex-end", marginTop: "24px" }}>
              <button
                style={styles.secondaryButton}
                onClick={() => setShowDetailsModal(false)}
              >
                {t.close}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* AlertModal Component */}
      <AlertModal
        open={alertModal.open}
        title={alertModal.title}
        message={alertModal.message}
        variant={alertModal.variant}
        showCancel={alertModal.showCancel}
        onConfirm={() => {
          if (alertModal.onConfirm) alertModal.onConfirm();
          closeAlert();
        }}
        onClose={closeAlert}
        theme={theme}
        language={language}
      />
    </div>
  );
};

export default Mo3tiAdvancementManagement;
